import {
  Color,
  ColorRepresentation,
  CylinderGeometry,
  Material,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  SphereGeometry,
  Vector2,
  WebGLRenderer
} from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { getAnnotationScale } from '../../annotation-scale';
import {
  ANNOTATION_PREVIEW_OPACITY,
  BUILD_DIRECTION_ARROW_COLOR,
  CENTER_OF_GRAVITY_TARGET_COLOR,
  CONSTRAINT_DEFAULT_COLOR,
  CONSTRAINT_FREE_OPACITY,
  DRAW_DIRECTION_ARROW_COLOR,
  GROUP_EDIT_CONTROL_COLOR,
  LINK_LINE_COLOR,
  LOAD_INDICATOR_BBOX_EPSILON,
  LOAD_INDICATOR_DEFAULT_LENGTH,
  LOAD_INDICATOR_SCALE_FACTOR,
  LOAD_SPHERE_HEIGHT_SEGMENTS,
  LOAD_SPHERE_RADIUS,
  LOAD_SPHERE_WIDTH_SEGMENTS,
  MASS_SPHERE_COLOR,
  MAX_STRESS_MARKER_COLOR,
  OPEN_DIRECTION_ARROW_COLOR,
  PART_FORCE_ARROW_COLOR,
  PART_MOMENT_ARROW_COLOR,
  REACTION_FORCE_ARROW_COLOR,
  REFERENCE_POINT_COLOR,
  REMOTE_POINT_SPHERE_COLOR
} from '../../engine.constants';

// ── Colour roles ─────────────────────────────────────────────────────

export const OVERLAY_COLORS = {
  force: PART_FORCE_ARROW_COLOR,
  moment: PART_MOMENT_ARROW_COLOR,
  remotePointSphere: REMOTE_POINT_SPHERE_COLOR,
  massSphere: MASS_SPHERE_COLOR,
  referencePoint: REFERENCE_POINT_COLOR,
  link: LINK_LINE_COLOR,
  groupEditControl: GROUP_EDIT_CONTROL_COLOR,
  reactionForce: REACTION_FORCE_ARROW_COLOR,
  drawDirection: DRAW_DIRECTION_ARROW_COLOR,
  openDirection: OPEN_DIRECTION_ARROW_COLOR,
  buildDirection: BUILD_DIRECTION_ARROW_COLOR,
  maxStress: MAX_STRESS_MARKER_COLOR,
  constraint: CONSTRAINT_DEFAULT_COLOR,
  centerOfGravity: CENTER_OF_GRAVITY_TARGET_COLOR
} as const;

// ── States ───────────────────────────────────────────────────────────

/** hover also covers drag-active and selected. */
export type OverlayState = 'normal' | 'hover' | 'preview' | 'faint';

export const OVERLAY_OPACITY: Readonly<Record<OverlayState, number>> = {
  normal: 1,
  hover: 1,
  preview: ANNOTATION_PREVIEW_OPACITY,
  faint: CONSTRAINT_FREE_OPACITY
};

/** How far toward white the hover state mixes the base colour. */
export const OVERLAY_HOVER_LIGHTEN = 0.6;

// ── Depth / draw order ───────────────────────────────────────────────

/** Above translucent guides (symmetry planes, print plate) and authoring tools (990-1006). */
export const OVERLAY_RENDER_ORDER = 2000;
export const OVERLAY_LABEL_RENDER_ORDER = OVERLAY_RENDER_ORDER + 1;

// ── Lines ────────────────────────────────────────────────────────────

export const OVERLAY_LINE_WIDTH_CSS_PX = 3;
/** Free constraint axes, dashed connectors, guide lines. */
export const OVERLAY_THIN_LINE_WIDTH_CSS_PX = 1.5;

// ── Geometry ─────────────────────────────────────────────────────────

export const OVERLAY_RADIAL_SEGMENTS = LOAD_SPHERE_WIDTH_SEGMENTS;
export const OVERLAY_SPHERE_HEIGHT_SEGMENTS = LOAD_SPHERE_HEIGHT_SEGMENTS;

/** Head length as a share of an ArrowHelper length (its drawn length is half of that). */
export const ARROW_HEAD_LENGTH_RATIO = 0.2;
/** Head radius as a share of head length. */
export const ARROW_HEAD_RADIUS_RATIO = 0.2;
/** Head length as a share of the drawn arrow length. */
export const ARROW_HEAD_DRAWN_RATIO = ARROW_HEAD_LENGTH_RATIO * 2;

// ── Sizing ───────────────────────────────────────────────────────────

export const OVERLAY_MIN_MODEL_SIZE = 0.1;
export const OVERLAY_MAX_MODEL_SIZE = 100_000;
/** Model size assumed for an empty scene; gives the 40-unit load arrow. */
export const OVERLAY_FALLBACK_MODEL_SIZE = LOAD_INDICATOR_DEFAULT_LENGTH / LOAD_INDICATOR_SCALE_FACTOR;

/** Fractions of the overlay unit. */
export const OVERLAY_SIZE = {
  loadArrow: LOAD_INDICATOR_SCALE_FACTOR,
  handleRadius: LOAD_INDICATOR_SCALE_FACTOR * (LOAD_SPHERE_RADIUS / LOAD_INDICATOR_DEFAULT_LENGTH),
  pointRadius: 0.015,
  triadArm: 0.15,
  crossHalf: 0.08,
  headMax: LOAD_INDICATOR_SCALE_FACTOR * ARROW_HEAD_LENGTH_RATIO
} as const;

export interface OverlaySize {
  x: number;
  y: number;
  z: number;
}

/** Largest bbox dimension, clamped; the fallback for an empty or degenerate scene. */
export function overlayModelSize(size: OverlaySize | null | undefined): number {
  const maxDim = size ? Math.max(size.x, size.y, size.z) : Number.NaN;
  if (!Number.isFinite(maxDim) || maxDim <= LOAD_INDICATOR_BBOX_EPSILON) return OVERLAY_FALLBACK_MODEL_SIZE;
  return Math.min(OVERLAY_MAX_MODEL_SIZE, Math.max(OVERLAY_MIN_MODEL_SIZE, maxDim));
}

/** The unit every overlay size is a fraction of. */
export function overlayUnit(size: OverlaySize | null | undefined, followAnnotationScale = true): number {
  return overlayModelSize(size) * (followAnnotationScale ? getAnnotationScale() : 1);
}

/** ArrowHelper length of a load arrow. */
export function loadArrowLength(size: OverlaySize | null | undefined): number {
  return overlayUnit(size) * OVERLAY_SIZE.loadArrow;
}

/** Radius of a draggable load sphere. */
export function handleRadius(size: OverlaySize | null | undefined): number {
  return overlayUnit(size) * OVERLAY_SIZE.handleRadius;
}

/** Radius of a point marker; does not follow the annotation size. */
export function pointMarkerRadius(size: OverlaySize | null | undefined): number {
  return overlayUnit(size, false) * OVERLAY_SIZE.pointRadius;
}

export function triadArmLength(size: OverlaySize | null | undefined): number {
  return overlayUnit(size) * OVERLAY_SIZE.triadArm;
}

export function constraintCrossHalfLength(size: OverlaySize | null | undefined): number {
  return overlayUnit(size) * OVERLAY_SIZE.crossHalf;
}

export interface ArrowHeadSize {
  length: number;
  radius: number;
}

/** Head of an arrow drawn `drawnLength` long: a load arrow's head, smaller on short arrows. */
export function arrowHead(drawnLength: number, unit: number): ArrowHeadSize {
  const length = Math.max(0, Math.min(drawnLength * ARROW_HEAD_DRAWN_RATIO, unit * OVERLAY_SIZE.headMax));
  return { length, radius: length * ARROW_HEAD_RADIUS_RATIO };
}

// ── Materials ────────────────────────────────────────────────────────

type OverlayMaterial = MeshBasicMaterial | LineMaterial;

const BASE_COLOR_KEY = 'overlayBaseColor';
const STATE_KEY = 'overlayState';
const WIDTH_KEY = 'overlayWidthCssPx';
const WHITE = 0xffffff;

export function overlayStateColor(base: ColorRepresentation, state: OverlayState): Color {
  const color = new Color(base);
  return state === 'hover' ? color.lerp(new Color(WHITE), OVERLAY_HOVER_LIGHTEN) : color;
}

function initMaterial(material: OverlayMaterial, color: ColorRepresentation, state: OverlayState): void {
  material.userData[BASE_COLOR_KEY] = new Color(color).getHex();
  applyOverlayState(material, state);
}

/** Recolours and fades a material for `state`, from its stored base colour. */
export function applyOverlayState(material: OverlayMaterial, state: OverlayState): void {
  const base = material.userData[BASE_COLOR_KEY];
  material.userData[STATE_KEY] = state;
  material.color.copy(overlayStateColor(typeof base === 'number' ? base : material.color.getHex(), state));
  material.opacity = OVERLAY_OPACITY[state];
}

export function overlayStateOf(material: Material): OverlayState {
  const state = material.userData[STATE_KEY];
  return state === 'hover' || state === 'preview' || state === 'faint' ? state : 'normal';
}

/** New base colour, keeping the current state. */
export function setOverlayBaseColor(material: OverlayMaterial, color: ColorRepresentation): void {
  material.userData[BASE_COLOR_KEY] = new Color(color).getHex();
  applyOverlayState(material, overlayStateOf(material));
}

function isOverlayMaterial(material: unknown): material is OverlayMaterial {
  return material instanceof MeshBasicMaterial || material instanceof LineMaterial;
}

/** Applies `state` to every overlay material under `object`. */
export function setOverlayState(object: Object3D, state: OverlayState): void {
  object.traverse((child) => {
    const material = (child as Partial<Mesh>).material;
    const materials = Array.isArray(material) ? material : material ? [material] : [];
    for (const each of materials) {
      if (isOverlayMaterial(each)) applyOverlayState(each, state);
    }
  });
}

/** Unlit, so flat by construction; drawn on top. */
export function createOverlayMeshMaterial(color: ColorRepresentation, state: OverlayState = 'normal'): MeshBasicMaterial {
  const material = new MeshBasicMaterial({
    toneMapped: false,
    depthTest: false,
    depthWrite: false,
    transparent: true
  });
  initMaterial(material, color, state);
  return material;
}

export interface OverlayLineOptions {
  widthCssPx?: number;
  state?: OverlayState;
  dashed?: boolean;
}

export function createOverlayLineMaterial(color: ColorRepresentation, options: OverlayLineOptions = {}): LineMaterial {
  const width = options.widthCssPx ?? OVERLAY_LINE_WIDTH_CSS_PX;
  const material = new LineMaterial({
    linewidth: width,
    dashed: options.dashed ?? false,
    toneMapped: false,
    depthTest: false,
    depthWrite: false,
    transparent: true
  });
  material.userData[WIDTH_KEY] = width;
  initMaterial(material, color, options.state ?? 'normal');
  return material;
}

/** Drawing-buffer pixels per CSS pixel of the renderer's canvas. */
export function bufferPxPerCssPx(bufferWidth: number, cssWidth: number): number {
  return cssWidth > 0 && bufferWidth > 0 ? bufferWidth / cssWidth : 1;
}

/** Line2 whose width stays in CSS px at any DPR, render scale or capture size. */
export class OverlayLine extends Line2 {
  private readonly bufferSize = new Vector2();

  constructor(geometry: LineGeometry, material: LineMaterial) {
    super(geometry, material);
    this.renderOrder = OVERLAY_RENDER_ORDER;
    this.computeLineDistances();
  }

  override onBeforeRender(renderer: WebGLRenderer): void {
    super.onBeforeRender(renderer);
    const width = this.material.userData[WIDTH_KEY];
    if (typeof width !== 'number') return;
    const buffer = renderer.getDrawingBufferSize(this.bufferSize);
    this.material.linewidth = width * bufferPxPerCssPx(buffer.x, renderer.domElement.clientWidth);
  }
}

export function createOverlayLine(positions: number[], material: LineMaterial): OverlayLine {
  const geometry = new LineGeometry();
  geometry.setPositions(positions);
  return new OverlayLine(geometry, material);
}

// ── Geometry factories ───────────────────────────────────────────────

/** Unit cone, tip at +0.5 on Y. Scale (radius, length, radius). */
export function createOverlayConeGeometry(): CylinderGeometry {
  return new CylinderGeometry(0, 1, 1, OVERLAY_RADIAL_SEGMENTS, 1);
}

export function createOverlaySphereGeometry(radius: number): SphereGeometry {
  return new SphereGeometry(radius, OVERLAY_RADIAL_SEGMENTS, OVERLAY_SPHERE_HEIGHT_SEGMENTS);
}

export function createOverlaySphere(
  radius: number,
  color: ColorRepresentation,
  state: OverlayState = 'normal'
): Mesh<SphereGeometry, MeshBasicMaterial> {
  const mesh = new Mesh(createOverlaySphereGeometry(radius), createOverlayMeshMaterial(color, state));
  mesh.renderOrder = OVERLAY_RENDER_ORDER;
  return mesh;
}

/** Cone mesh for an arrow head, positioned by the caller. */
export function createOverlayCone(color: ColorRepresentation, head: ArrowHeadSize, state: OverlayState = 'normal'): Mesh {
  const mesh = new Mesh(createOverlayConeGeometry(), createOverlayMeshMaterial(color, state));
  mesh.scale.set(head.radius, head.length, head.radius);
  mesh.renderOrder = OVERLAY_RENDER_ORDER;
  return mesh;
}

/** Disposes every geometry and material under `object` and detaches it. */
export function disposeOverlay(object: Object3D): void {
  object.removeFromParent();
  object.traverse((child) => {
    const mesh = child as Partial<Mesh>;
    mesh.geometry?.dispose();
    const material = mesh.material;
    const materials = Array.isArray(material) ? material : material ? [material] : [];
    for (const each of materials) each.dispose();
  });
}
